import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '../hooks/useTasks';
import { useOpportunities } from '../hooks/useOpportunities';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '../context/AuthContext';
import { usePOs } from '../hooks/usePOs';
import { useCompanyLogos } from '../hooks/useCompanyLogos';
import OpportunityModal from '../components/modals/OpportunityModal';
import { 
  CheckSquare, 
  Square, 
  Search, 
  Trash2, 
  CheckCircle2, 
  AlertCircle,
  ExternalLink,
  Plus,
  Briefcase,
  Loader2,
  FileSpreadsheet
} from 'lucide-react';

export default function Tasks() {
  const navigate = useNavigate();
  const { 
    tasks, 
    loading: tasksLoading, 
    toggleTaskComplete, 
    deleteTask, 
    addTask,
    ensureWeeklyTouchTasks 
  } = useTasks();

  const { opportunities, loading: oppsLoading } = useOpportunities();
  const { users, loading: usersLoading } = useUsers();
  const { currentUser } = useAuth();
  const { pos, loading: posLoading } = usePOs();
  const { companyLogos } = useCompanyLogos();

  const currentUserObj = useMemo(() => {
    if (!currentUser) return null;
    return (users || []).find(u => 
      (u.email && currentUser.email && u.email.toLowerCase() === currentUser.email.toLowerCase()) || 
      u.id === currentUser.uid
    );
  }, [currentUser, users]);

  const currentUserName = useMemo(() => {
    if (currentUserObj?.name) return currentUserObj.name;
    if (currentUser?.displayName) return currentUser.displayName;
    if (currentUser?.email) {
      const prefix = currentUser.email.split('@')[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return '';
  }, [currentUser, currentUserObj]);

  const userRoles = useMemo(() => {
    return currentUserObj?.roles || (currentUserObj?.role ? [currentUserObj.role] : []);
  }, [currentUserObj]);

  const isAdmin = useMemo(() => userRoles.includes("Admin"), [userRoles]);
  const isBD = useMemo(() => userRoles.includes("Business Development") || userRoles.includes("Admin"), [userRoles]);
  const isProcurementOrLogisticsOrWarehousing = useMemo(() => {
    return userRoles.some(r => ["Procurement", "Logistics", "Warehousing", "Admin"].includes(r));
  }, [userRoles]);

  const currentUserAliases = useMemo(() => {
    const aliases = new Set();
    if (currentUserObj?.name) aliases.add(currentUserObj.name.trim().toLowerCase());
    if (currentUser?.displayName) aliases.add(currentUser.displayName.trim().toLowerCase());
    if (currentUser?.email) {
      aliases.add(currentUser.email.trim().toLowerCase());
      const prefix = currentUser.email.split('@')[0].toLowerCase();
      aliases.add(prefix);
    }
    if (currentUser?.uid) aliases.add(currentUser.uid.toLowerCase());
    return Array.from(aliases).filter(Boolean);
  }, [currentUser, currentUserObj]);

  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending'); // 'all' | 'pending' | 'completed'
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterType, setFilterType] = useState('all'); // 'all' | 'weekly_touch' | 'custom'
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskOppId, setNewTaskOppId] = useState('');
  const [newTaskEstimateId, setNewTaskEstimateId] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');

  // Run weekly touch task generation on mount
  useEffect(() => {
    ensureWeeklyTouchTasks();
  }, []);

  const allActiveUsers = useMemo(() => {
    return (users || [])
      .filter(u => !u.deleted && u.status !== 'Disabled')
      .map(u => u.name || (u.email ? u.email.split('@')[0] : 'Unnamed User'))
      .sort((a, b) => a.localeCompare(b));
  }, [users]);

  // Won and unpaid estimates scoped to companies the user is permitted to see
  const wonUnpaidEstimates = useMemo(() => {
    if (!isProcurementOrLogisticsOrWarehousing) return [];
    
    return (pos || []).filter(po => {
      const isWon = (po.status || '').toLowerCase() === 'won';
      if (!isWon) return false;

      const isPaid = po.paidStatus === 'Paid' || po.paymentStatus === 'Paid';
      if (isPaid) return false;

      if (isAdmin) return true;

      const allowedNames = new Set((companyLogos || []).map(c => (c.name || '').toLowerCase()));
      const allowedIds = new Set((companyLogos || []).map(c => c.id).filter(Boolean));
      const allowedUrls = new Set((companyLogos || []).map(c => c.url).filter(Boolean));

      if (po.companyId && allowedIds.has(po.companyId)) return true;
      if (po.logoUrl && allowedUrls.has(po.logoUrl)) return true;
      const compName = (po.companyName || po.company || '').toLowerCase();
      if (compName && allowedNames.has(compName)) return true;

      if (!po.companyId && !po.logoUrl && !compName) return true;

      return false;
    });
  }, [pos, isProcurementOrLogisticsOrWarehousing, isAdmin, companyLogos]);

  // Helper to match task to assignee filter
  const isTaskMatchingAssignee = (task, targetAssignee) => {
    if (!targetAssignee || targetAssignee === 'all') return true;
    const taskAssigned = (task.assignedTo || '').trim().toLowerCase();
    const target = targetAssignee.trim().toLowerCase();

    if (target === 'unassigned') {
      return taskAssigned === '';
    }

    const isTargetCurrentUser = target === currentUserName.toLowerCase() || currentUserAliases.includes(target);
    if (isTargetCurrentUser) {
      if (currentUserAliases.includes(taskAssigned)) return true;
    }

    return taskAssigned === target;
  };

  // Task counts per assignee
  const assigneeCounts = useMemo(() => {
    const counts = { all: tasks.length };
    tasks.forEach(t => {
      const assigned = (t.assignedTo || '').trim() || 'Unassigned';
      counts[assigned] = (counts[assigned] || 0) + 1;
    });
    return counts;
  }, [tasks]);

  const myTasksCount = useMemo(() => {
    if (!currentUserName) return 0;
    return tasks.filter(t => isTaskMatchingAssignee(t, currentUserName)).length;
  }, [tasks, currentUserName, currentUserAliases]);

  // Active opportunities for new task dropdown
  const activeOpportunities = useMemo(() => {
    return (opportunities || []).filter(opp => {
      const statusLower = (opp.status || '').trim().toLowerCase();
      const isArchived = opp.archived === true || opp.archived === 'true' || statusLower === 'archived';
      return !isArchived && statusLower !== 'lost';
    });
  }, [opportunities]);

  // Map oppId to full opportunity object for fast lookup
  const oppMap = useMemo(() => {
    const map = new Map();
    (opportunities || []).forEach(opp => {
      map.set(opp.id, opp);
    });
    return map;
  }, [opportunities]);

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // 1. Assignee filtering
      if (!isAdmin) {
        // Non-admins strictly see only their own tasks
        if (!isTaskMatchingAssignee(task, currentUserName)) {
          return false;
        }
      } else {
        // Admins can filter by any assignee
        if (filterAssignee && filterAssignee !== 'all') {
          if (!isTaskMatchingAssignee(task, filterAssignee)) {
            return false;
          }
        }
      }

      // 2. Status matching (pending / completed / all)
      if (filterStatus === 'pending' && task.completed) return false;
      if (filterStatus === 'completed' && !task.completed) return false;

      // 3. Type matching (weekly_touch / custom / all)
      if (filterType === 'weekly_touch' && task.type !== 'weekly_touch') return false;
      if (filterType === 'custom' && task.type !== 'custom') return false;

      // 4. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const titleMatch = (task.title || '').toLowerCase().includes(q);
        const oppMatch = (task.oppName || '').toLowerCase().includes(q);
        const estMatch = (task.estimateName || task.estimateNumber || '').toLowerCase().includes(q);
        const assignedMatch = (task.assignedTo || '').toLowerCase().includes(q);
        if (!titleMatch && !oppMatch && !estMatch && !assignedMatch) return false;
      }

      return true;
    });
  }, [tasks, isAdmin, currentUserName, filterAssignee, filterStatus, filterType, searchQuery, currentUserAliases]);

  const handleTaskClick = (task) => {
    if (task.oppId) {
      const fullOpp = oppMap.get(task.oppId);
      if (fullOpp) {
        setSelectedOpportunity(fullOpp);
      } else {
        setSelectedOpportunity({
          id: task.oppId,
          name: task.oppName || 'Opportunity Details',
          assignedTo: task.assignedTo || ''
        });
      }
    } else if (task.estimateNumber || task.estimateId) {
      navigate('/procurement');
    }
  };

  const handleCreateNewTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;

    const selectedOpp = isBD && newTaskOppId ? oppMap.get(newTaskOppId) : null;
    const selectedEst = isProcurementOrLogisticsOrWarehousing && newTaskEstimateId ? (pos || []).find(p => p.id === newTaskEstimateId) : null;

    try {
      await addTask({
        title: newTaskTitle.trim(),
        oppId: isBD ? (newTaskOppId || '') : '',
        oppName: isBD && selectedOpp ? (selectedOpp.shortDesc || selectedOpp.name) : '',
        estimateId: isProcurementOrLogisticsOrWarehousing ? (newTaskEstimateId || '') : '',
        estimateNumber: isProcurementOrLogisticsOrWarehousing && selectedEst ? (selectedEst.poNumber || '') : '',
        estimateName: isProcurementOrLogisticsOrWarehousing && selectedEst ? `${selectedEst.poNumber}${selectedEst.clientName ? ' - ' + selectedEst.clientName : ''}` : '',
        assignedTo: newTaskAssignee || currentUserName || '',
        dueDate: newTaskDueDate || '',
        completed: false,
        type: 'custom'
      });
      setNewTaskTitle('');
      setNewTaskOppId('');
      setNewTaskEstimateId('');
      setNewTaskAssignee('');
      setNewTaskDueDate('');
      setShowAddModal(false);
    } catch (err) {
      console.error("Failed to create task", err);
    }
  };

  const pendingCount = tasks.filter(t => !t.completed).length;
  const completedCount = tasks.filter(t => t.completed).length;

  return (
    <div className="page-container" style={{ padding: "1.5rem", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header & Controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h2 style={{ color: "var(--primary)", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <CheckSquare size={24} /> Daily Task List
          </h2>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          {isBD && (
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate('/opportunities?tab=active', { state: { tab: 'active' } })}
              style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 600 }}
            >
              <Briefcase size={16} /> Go To Opps
            </button>
          )}
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={() => {
              setNewTaskAssignee(currentUserName || '');
              setShowAddModal(true);
            }}
            style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 600 }}
          >
            <Plus size={16} /> New Task
          </button>
        </div>
      </div>

      {/* Toolbar & Filters */}
      <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1.5rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
        {/* Status Filter Tabs */}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button 
            type="button" 
            className={`btn ${filterStatus === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: "0.8125rem", padding: "0.4rem 0.85rem" }}
            onClick={() => setFilterStatus('pending')}
          >
            Pending ({isAdmin ? pendingCount : tasks.filter(t => !t.completed && isTaskMatchingAssignee(t, currentUserName)).length})
          </button>
          <button 
            type="button" 
            className={`btn ${filterStatus === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: "0.8125rem", padding: "0.4rem 0.85rem" }}
            onClick={() => setFilterStatus('all')}
          >
            All ({isAdmin ? tasks.length : myTasksCount})
          </button>
          <button 
            type="button" 
            className={`btn ${filterStatus === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: "0.8125rem", padding: "0.4rem 0.85rem" }}
            onClick={() => setFilterStatus('completed')}
          >
            Completed ({isAdmin ? completedCount : tasks.filter(t => t.completed && isTaskMatchingAssignee(t, currentUserName)).length})
          </button>
        </div>

        {/* Search */}
        <div className="input-with-icon" style={{ flex: 1, minWidth: "220px" }}>
          <Search className="input-icon" size={18} />
          <input
            type="text"
            className="form-input icon-padding"
            placeholder="Search tasks by description, record, or assignee..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ margin: 0 }}
          />
        </div>

        {/* Assignee Filter Dropdown (ADMIN ONLY) */}
        {isAdmin && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span className="text-secondary" style={{ fontSize: "0.875rem", fontWeight: 500 }}>Assignee:</span>
            <select
              className="form-input"
              value={filterAssignee || 'all'}
              onChange={e => setFilterAssignee(e.target.value)}
              style={{ margin: 0, padding: "0.4rem 1.5rem 0.4rem 1rem", fontSize: "0.875rem", height: "38px", backgroundColor: "var(--bg-card)", minWidth: "180px" }}
            >
              <option value="all">All Assignees ({tasks.length})</option>
              {currentUserName && (
                <option value={currentUserName}>
                  My Tasks ({myTasksCount})
                </option>
              )}
              {allActiveUsers.filter(u => u !== currentUserName).map(name => (
                <option key={name} value={name}>
                  {name} ({assigneeCounts[name] || 0})
                </option>
              ))}
              {assigneeCounts['Unassigned'] > 0 && (
                <option value="Unassigned">
                  Unassigned ({assigneeCounts['Unassigned'] || 0})
                </option>
              )}
            </select>
          </div>
        )}

        {/* Type Filter Dropdown */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span className="text-secondary" style={{ fontSize: "0.875rem", fontWeight: 500 }}>Type:</span>
          <select
            className="form-input"
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            style={{ margin: 0, padding: "0.4rem 1.5rem 0.4rem 1rem", fontSize: "0.875rem", height: "38px", backgroundColor: "var(--bg-card)" }}
          >
            <option value="all">All Types</option>
            <option value="weekly_touch">Weekly Touches</option>
            <option value="custom">Manual Tasks</option>
          </select>
        </div>
      </div>

      {/* Tasks Table */}
      {tasksLoading || oppsLoading || usersLoading || posLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "4rem" }}>
          <Loader2 className="spinner-icon" size={32} />
          <span style={{ marginLeft: "0.75rem", color: "var(--text-secondary)" }}>Loading tasks...</span>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="card glass-card" style={{ padding: "3rem", textAlign: "center", color: "var(--text-secondary)" }}>
          <CheckCircle2 size={40} style={{ margin: "0 auto 1rem auto", opacity: 0.4, color: "var(--primary)" }} />
          <div style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-primary)", marginBottom: "0.25rem" }}>No tasks found</div>
          <p style={{ margin: "0 0 1rem 0", fontSize: "0.875rem", fontStyle: "italic" }}>
            {searchQuery 
              ? "No tasks matching your search." 
              : filterStatus === 'pending' 
                ? "You're all caught up! No pending tasks." 
                : "No tasks in this category."}
          </p>
          {isAdmin && filterAssignee && filterAssignee !== 'all' && tasks.length > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: "0.8125rem", padding: "0.35rem 0.75rem" }}
              onClick={() => setFilterAssignee('all')}
            >
              View All Assignees ({tasks.length})
            </button>
          )}
        </div>
      ) : (
        <div className="card glass-card" style={{ padding: 0, overflowX: "auto" }}>
          <table className="table" style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border-color)", backgroundColor: "rgba(255,255,255,0.01)" }}>
                <th style={{ padding: "1rem 1.25rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)", width: "50px", textAlign: "center" }}>Done</th>
                <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Task Description</th>
                <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Associated Record</th>
                <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Assigned Lead</th>
                <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Due Date</th>
                <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Type</th>
                <th style={{ padding: "1rem 1.5rem", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)", width: "80px", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(task => {
                const isTouch = task.type === 'weekly_touch';
                const opp = task.oppId ? oppMap.get(task.oppId) : null;
                const isOverdue = task.dueDate && !task.completed && new Date(task.dueDate + "T23:59:59") < new Date();

                return (
                  <tr 
                    key={task.id} 
                    style={{ 
                      borderBottom: "1px solid var(--border-color)", 
                      backgroundColor: task.completed ? "rgba(255,255,255,0.01)" : "transparent",
                      transition: "background-color 0.2s"
                    }}
                    className="hover-row"
                  >
                    {/* Checkbox */}
                    <td style={{ padding: "1rem 1.25rem", textAlign: "center", verticalAlign: "middle" }}>
                      <button
                        type="button"
                        onClick={() => toggleTaskComplete(task.id, task.completed, currentUserName)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: task.completed ? "#10B981" : "var(--text-secondary)", padding: 0, display: "inline-flex", alignItems: "center" }}
                        title={task.completed ? "Mark as Incomplete" : "Clear / Complete Task"}
                      >
                        {task.completed ? <CheckSquare size={19} /> : <Square size={19} />}
                      </button>
                    </td>

                    {/* Title & Click to open */}
                    <td style={{ padding: "1rem 1.5rem", verticalAlign: "middle" }}>
                      <div 
                        onClick={() => handleTaskClick(task)}
                        style={{ 
                          cursor: (task.oppId || task.estimateNumber || task.estimateId) ? "pointer" : "default",
                          fontWeight: 500,
                          color: task.completed ? "var(--text-secondary)" : "var(--text-primary)",
                          textDecoration: task.completed ? "line-through" : "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.4rem"
                        }}
                        title={(task.oppId || task.estimateNumber) ? "Click to view details" : undefined}
                      >
                        {task.title}
                        {(task.oppId || task.estimateNumber) && <ExternalLink size={13} style={{ opacity: 0.6 }} />}
                      </div>
                    </td>

                    {/* Associated Record (Opportunity or Estimate) */}
                    <td style={{ padding: "1rem 1.5rem", verticalAlign: "middle" }}>
                      {task.oppId ? (
                        <button
                          type="button"
                          onClick={() => handleTaskClick(task)}
                          style={{ 
                            background: "none", 
                            border: "none", 
                            padding: 0, 
                            color: "var(--primary)", 
                            fontSize: "0.85rem", 
                            cursor: "pointer",
                            fontWeight: 500,
                            textAlign: "left",
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.35rem"
                          }}
                          title="Click to view Opportunity details"
                        >
                          <Briefcase size={13} />
                          <span>{task.oppName || (opp ? (opp.shortDesc || opp.name) : "View Opportunity")}</span>
                        </button>
                      ) : (task.estimateName || task.estimateNumber) ? (
                        <button
                          type="button"
                          onClick={() => navigate('/procurement')}
                          style={{ 
                            background: "none", 
                            border: "none", 
                            padding: 0, 
                            color: "var(--accent)", 
                            fontSize: "0.85rem", 
                            cursor: "pointer",
                            fontWeight: 500,
                            textAlign: "left",
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.35rem"
                          }}
                          title="Click to view in Procurement"
                        >
                          <FileSpreadsheet size={13} />
                          <span>{task.estimateName || task.estimateNumber}</span>
                        </button>
                      ) : (
                        <span style={{ color: "var(--text-muted)", fontSize: "0.8rem", fontStyle: "italic" }}>General Task</span>
                      )}
                    </td>

                    {/* Assignee */}
                    <td style={{ padding: "1rem 1.5rem", verticalAlign: "middle" }}>
                      {task.assignedTo ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.85rem", fontWeight: 500, color: "var(--text-primary)" }}>
                          <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "var(--primary)", display: "inline-block" }}></span>
                          {task.assignedTo}
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)", fontStyle: "italic", fontSize: "0.8rem" }}>Unassigned</span>
                      )}
                    </td>

                    {/* Due Date */}
                    <td style={{ padding: "1rem 1.5rem", verticalAlign: "middle" }}>
                      {task.dueDate ? (
                        <span style={{ 
                          fontSize: "0.85rem", 
                          color: isOverdue ? "var(--danger)" : "var(--text-secondary)",
                          fontWeight: isOverdue ? 600 : 400,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.3rem"
                        }}>
                          {isOverdue && <AlertCircle size={14} />}
                          {task.dueDate}
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)", fontStyle: "italic", fontSize: "0.8rem" }}>None</span>
                      )}
                    </td>

                    {/* Type Badge */}
                    <td style={{ padding: "1rem 1.5rem", verticalAlign: "middle" }}>
                      {isTouch ? (
                        <span className="badge" style={{ fontSize: "0.72rem", backgroundColor: "rgba(59, 130, 246, 0.15)", color: "#3B82F6", border: "1px solid rgba(59, 130, 246, 0.3)" }}>
                          Weekly Touch
                        </span>
                      ) : (
                        <span className="badge badge-secondary" style={{ fontSize: "0.72rem" }}>
                          Custom
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: "1rem 1.5rem", textAlign: "right", verticalAlign: "middle" }}>
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            if (window.confirm("Delete this task?")) {
                              deleteTask(task.id);
                            }
                          }}
                          style={{ padding: "0.35rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                          title="Delete Task"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div className="card glass-card" style={{ width: "520px", maxWidth: "100%", padding: "1.5rem", backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)", borderRadius: "8px" }}>
            <h3 style={{ margin: "0 0 1.25rem 0", color: "var(--primary)" }}>Create New Task</h3>
            <form onSubmit={handleCreateNewTask}>
              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label className="form-label">Task Description *</label>
                <input
                  required
                  className="form-input"
                  placeholder="e.g. Follow up on item shipment, call client..."
                  value={newTaskTitle}
                  onChange={e => setNewTaskTitle(e.target.value)}
                  autoFocus
                />
              </div>

              {/* Associated Opportunity (Business Development Only) */}
              {isBD && (
                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label className="form-label">Associated Opportunity (Optional)</label>
                  <select
                    className="form-input"
                    value={newTaskOppId}
                    onChange={e => {
                      const oppId = e.target.value;
                      setNewTaskOppId(oppId);
                      if (oppId) {
                        setNewTaskEstimateId(''); // mutually clear estimate if opp chosen
                        const opp = oppMap.get(oppId);
                        if (opp && opp.assignedTo && !newTaskAssignee) {
                          setNewTaskAssignee(opp.assignedTo);
                        }
                      }
                    }}
                  >
                    <option value="">-- None / General Task --</option>
                    {activeOpportunities.map(opp => (
                      <option key={opp.id} value={opp.id}>
                        {opp.shortDesc || opp.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Estimates (Procurement / Logistics / Warehousing Only) */}
              {isProcurementOrLogisticsOrWarehousing && (
                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label className="form-label">Estimates (Won & Unpaid)</label>
                  <select
                    className="form-input"
                    value={newTaskEstimateId}
                    onChange={e => {
                      const estId = e.target.value;
                      setNewTaskEstimateId(estId);
                      if (estId) {
                        setNewTaskOppId(''); // mutually clear opp if estimate chosen
                      }
                    }}
                  >
                    <option value="">-- None / General Task --</option>
                    {wonUnpaidEstimates.map(est => (
                      <option key={est.id} value={est.id}>
                        {est.poNumber} {est.clientName ? `- ${est.clientName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Assignee */}
              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label className="form-label">Assignee</label>
                <select
                  className="form-input"
                  value={newTaskAssignee}
                  onChange={e => setNewTaskAssignee(e.target.value)}
                >
                  <option value="">-- Select Assignee --</option>
                  {allActiveUsers.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {/* Due Date */}
              <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={newTaskDueDate}
                  onChange={e => setNewTaskDueDate(e.target.value)}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Opportunity Modal Popup */}
      {selectedOpportunity && (
        <OpportunityModal
          opportunity={selectedOpportunity}
          onClose={() => setSelectedOpportunity(null)}
        />
      )}
    </div>
  );
}
